import { createService } from "@dreamonkey/vue-signalr";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";

import type { IProvisioningProgressEvent } from "@/types";

class SignalRService {
  private connection: HubConnection | null = null;
  private service: ReturnType<typeof createService> | null = null;
  private operationId: string | null = null;
  private closeHandler: ((error?: Error) => void) | null = null;

  private ensureConnected() {
    if (
      !this.connection ||
      this.connection.state !== HubConnectionState.Connected ||
      !this.service?.connected.value
    ) {
      throw new Error("SignalR not connected");
    }
  }

  async connect(hubUrl: string, accessToken: string): Promise<boolean> {
    if (this.connection?.state === HubConnectionState.Connected) return true;

    if (this.connection != null) {
      await this.connection.stop().catch(() => null);
      this.connection = null;
      this.operationId = null;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => accessToken,
        withCredentials: true
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();

    const service = createService({
      connection,
      autoOffInsideComponentScope: false,
      failFn: error => {
        this.closeHandler?.(error instanceof Error ? error : undefined);
      }
    });

    connection.onreconnected(() => {
      if (this.operationId) {
        this.joinOperationGroup(this.operationId).catch(() => null);
      }
    });

    await service.init();

    this.connection = connection;
    this.service = service;

    return true;
  }

  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected && !!this.service?.connected.value;
  }

  onClose(callback: (error?: Error) => void) {
    this.closeHandler = callback;
  }

  onProgress(callback: (payload: IProvisioningProgressEvent) => void) {
    this.service?.off("ReceiveProgress");
    this.service?.on("ReceiveProgress", (progress: number, step: string) => {
      callback({ progress, step });
    });
  }

  onCompleted(callback: (result: unknown) => void) {
    this.service?.off("ReceiveCompleted");
    this.service?.on("ReceiveCompleted", (result: unknown) => {
      callback(result);
    });
  }

  onError(callback: (errorMessage: string) => void) {
    this.service?.off("ReceiveError");
    this.service?.on("ReceiveError", (errorMessage: string) => {
      callback(errorMessage);
    });
  }

  async joinOperationGroup(operationId: string) {
    this.ensureConnected();
    this.operationId = operationId;
    await this.service?.invoke("JoinOperationGroup", operationId);
  }

  async leaveOperationGroup(operationId?: string) {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    const opId = operationId ?? this.operationId;
    if (!opId) return;

    await this.service?.invoke("LeaveOperationGroup", opId);
    this.operationId = null;
  }

  async disconnect() {
    if (!this.connection) return;
    await this.connection.stop();
    this.connection = null;
    this.service = null;
    this.operationId = null;
    this.closeHandler = null;
  }
}

export default new SignalRService();